/**
 * 打字机效果 Mixin
 * 负责实现 AI 消息的打字机动画效果
 * 依赖: messageManager.js
 * 
 * Token 队列架构：
 * - typewriterQueue 现在是数组，每个元素是一个 Token 对象
 * - Token 类型：
 *   - { type: 'text', content: '文本内容' }
 *   - { type: 'image', content: '![alt](url)' }
 * - 图片 Token 会一次性整块输出，文字 Token 按字符逐步输出
 */
export default {
  data() {
    return {
      typewriterTimer: null, // 打字机定时器
      typewriterQueue: [], // 打字机待显示队列（Token 数组）
      typewriterSpeed: 30, // 打字机速度（毫秒/批次）- 降低以提高流畅度
      typewriterBatchSize: 2, // 每次显示的字符数（默认2个字符）
    };
  },

  methods: {
    /**
     * 将文本解析为 Token 序列（文本 + 图片）
     * @param {string} text - 输入文本
     * @returns {Array<{type: 'text'|'image', content: string}>}
     */
    parseTextToTokens(text) {
      if (!text) return [];
      
      const tokens = [];
      const imagePattern = /!\[[^\]]*]\([^)]*\)/g;
      let lastIndex = 0;
      let match;

      while ((match = imagePattern.exec(text)) !== null) {
        // 添加图片前的文本（如果有）
        if (match.index > lastIndex) {
          const textBefore = text.slice(lastIndex, match.index);
          if (textBefore) {
            tokens.push({ type: 'text', content: textBefore });
          }
        }
        
        // 添加图片 Token
        tokens.push({ type: 'image', content: match[0] });
        lastIndex = match.index + match[0].length;
      }

      // 添加最后剩余的文本（如果有）
      if (lastIndex < text.length) {
        const textAfter = text.slice(lastIndex);
        if (textAfter) {
          tokens.push({ type: 'text', content: textAfter });
        }
      }

      return tokens;
    },

    /**
     * 启动打字机效果
     * @param {object} aiMessage - AI 消息对象
     */
    startTypewriter(aiMessage) {
      // 如果队列为空或定时器已存在，不需要启动
      if (this.typewriterQueue.length === 0 || this.typewriterTimer) {
        return;
      }

      const typewriterTick = () => {
        if (this.typewriterQueue.length === 0) {
          this.stopTypewriter();
          return;
        }

        // 取出队首 Token
        const currentToken = this.typewriterQueue[0];
        
        if (currentToken.type === 'image') {
          // 图片 Token：一次性整块输出
          aiMessage.displayedContent = (aiMessage.displayedContent || "") + currentToken.content;
          this.typewriterQueue.shift(); // 移除这个 Token
        } else if (currentToken.type === 'text') {
          // 文字 Token：按 batchSize 逐批输出
          const queueTotalLength = this.typewriterQueue.reduce((sum, token) => {
            return sum + (token.content ? token.content.length : 0);
          }, 0);
          
          let batchSize = this.typewriterBatchSize;
          if (queueTotalLength > 50) {
            batchSize = 5;
          } else if (queueTotalLength > 20) {
            batchSize = 3;
          }

          const chars = currentToken.content.slice(0, batchSize);
          aiMessage.displayedContent = (aiMessage.displayedContent || "") + chars;
          
          // 更新当前 Token 的剩余内容
          currentToken.content = currentToken.content.slice(batchSize);
          
          // 如果这个 Token 已经输出完毕，移除它
          if (currentToken.content.length === 0) {
            this.typewriterQueue.shift();
          }
        }

        // 自动滚动到底部（如果用户在底部附近）
        if (this.isNearBottom && this.isNearBottom()) {
          this.$nextTick(() => {
            if (this.scrollToBottom) {
              this.scrollToBottom();
            }
          });
        }

        // 触发视图更新
        this.$forceUpdate();

        // 继续下一次显示
        this.typewriterTimer = setTimeout(typewriterTick, this.typewriterSpeed);
      };

      // 立即开始第一次显示
      typewriterTick();
    },

    /**
     * 停止打字机效果
     */
    stopTypewriter() {
      if (!this.typewriterTimer) {
        return;
      }

      // 如果队列里还有未渲染的数据，找到对应的消息并一次性渲染出来
      if (this.typewriterQueue.length > 0 && this.currentMessageId) {
        const aiMessage = this.findAIMessage(this.currentMessageId);
        if (aiMessage) {
          // 将所有剩余 Token 的内容拼接起来
          const remainingContent = this.typewriterQueue
            .map(token => token.content || '')
            .join('');
          aiMessage.displayedContent =
            (aiMessage.displayedContent || "") + remainingContent;
          this.typewriterQueue = [];
          this.$forceUpdate();
        }
      }
      clearTimeout(this.typewriterTimer);
      this.typewriterTimer = null;
    },

    /**
     * 添加文本到打字机队列
     * @param {string} text - 要添加的文本
     */
    addToTypewriterQueue(text) {
      if (!text) return;
      
      // 将文本解析为 Token 序列
      const newTokens = this.parseTextToTokens(text);
      
      // 如果队列最后一个是 text Token，且新的第一个也是 text Token，合并它们
      if (this.typewriterQueue.length > 0 && newTokens.length > 0) {
        const lastToken = this.typewriterQueue[this.typewriterQueue.length - 1];
        const firstNewToken = newTokens[0];
        
        if (lastToken.type === 'text' && firstNewToken.type === 'text') {
          lastToken.content += firstNewToken.content;
          newTokens.shift(); // 移除已合并的第一个新 Token
        }
      }
      
      // 将剩余的新 Token 添加到队列
      this.typewriterQueue.push(...newTokens);
    },

    /**
     * 重置打字机状态
     */
    resetTypewriter() {
      this.typewriterQueue = [];
      this.stopTypewriter();
    },
  },

  beforeDestroy() {
    // 组件销毁前清理打字机定时器
    this.stopTypewriter();
  },
};
